import "./style.css";
import "leaflet/dist/leaflet.css";
import { createWebHistory } from "vue-router";

import { createClientApp } from "./app";
import { createAnalyticsEnabler, enableAnalyticsKey } from "@/lib/analytics";
import { initializeSentry } from "@/monitoring";

type SupportedLocale = "pl" | "en";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const LOCALE_MAX_AGE = 60 * 60 * 24 * 365;

const appRoot = document.getElementById("app");
if (!appRoot) {
  throw new Error('Missing <div id="app"> mount element.');
}

const serverLocale = appRoot.dataset.locale;
// The shared i18n singleton resolves its initial locale from data-locale, so the
// app hydrates in the same locale the SSR server rendered with. We reconcile a
// differing stored preference *after* mount to avoid hydration mismatches.
const { app, router } = createClientApp(createWebHistory(), true);
initializeSentry(app, router);
const enableAnalytics = createAnalyticsEnabler(app, router);

app.provide(enableAnalyticsKey, enableAnalytics);

let recaptchaInstalled = false;

router.beforeEach(async (to) => {
  if (to.name !== "order" || recaptchaInstalled || !RECAPTCHA_SITE_KEY) return;

  const { VueReCaptcha } = await import("vue-recaptcha-v3");
  app.use(VueReCaptcha, {
    siteKey: RECAPTCHA_SITE_KEY,
    loaderOptions: { autoHideBadge: true },
  });
  recaptchaInstalled = true;
});

router.isReady().then(() => {
  app.mount("#app");

  const storedLocale = getStoredLocale();
  if (storedLocale && storedLocale !== serverLocale) {
    setLocale(storedLocale);
  }

  if (localStorage.getItem("cookie-consent") === "accepted") {
    void enableAnalytics();
  }
});

function getStoredLocale(): SupportedLocale | undefined {
  const locale = localStorage.getItem("locale");

  if (locale === "pl" || locale === "en") return locale;

  return undefined;
}

function setLocale(locale: SupportedLocale) {
  const globalI18n = app.config.globalProperties.$i18n as {
    locale: SupportedLocale | { value: SupportedLocale };
  };

  if (typeof globalI18n.locale === "object" && globalI18n.locale !== null) {
    globalI18n.locale.value = locale;
  } else {
    globalI18n.locale = locale;
  }

  // Persist to a cookie so the next SSR request renders in the same locale.
  document.cookie = `locale=${locale}; path=/; max-age=${LOCALE_MAX_AGE}; SameSite=Lax`;
}
